import { ChangeEvent, useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { apiPaths } from '../../../../../service/apiPaths';
import ApiService from '../../../../../service/ApiService';
import { Roles } from '../../../../../util/constant';
import { IAddTaskSheet } from '../../Tasksheet.props';

const useAddTaskSheet = () => {
  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState<IAddTaskSheet>({
    description: "",
    priority: "low",
    assignedTo: "",
    dueDate: today,
    status: "todo",
    projectId: "",
    videoUrl: "",
    isUserVideoReq: false,
  });
  const [tasks, setTasks] = useState<IAddTaskSheet[]>(() => {
    const savedTasks = localStorage.getItem("tasks");
    return savedTasks ? JSON.parse(savedTasks) : [];
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [defaultPriority, setDefaultPriority] = useState<string>("low");
  const [defaultStatus, setDefaultStatus] = useState<string>("todo");
  const [user, setUser] = useState<any[]>([]);
  const [userId, setUserId] = useState<string>("");
  const [project, setProject] = useState<any[]>([]);
  const [projectId, setProjectId] = useState<string>("");
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [isVideo, setIsVideo] = useState<boolean>(false);
  const navigate = useNavigate();
  const subRole = localStorage.getItem("subRole");
  const userTokenId = localStorage.getItem("userId");

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value, name } = e.target;
    setFormData({ ...formData, [id || name]: value });
  };

  useEffect(() => {
    fetchData();
    fetchProjectData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getEmployees}`,
      });
      setUser(result);
      if (subRole === Roles.tl && userTokenId) {
        console.log("userTokenId", userTokenId);
        setUserId(userTokenId);
      } else {
        console.log("result", result);
        setUserId(result[0].id);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getProjects}`,
      });
      setProject(result);
      setProjectId(result[0].id);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  // Add or update a task
  const addOrUpdateTask = () => {
    if (tasks.length >= 10 && editIndex === null) {
      toast.error("You can only add a maximum of 10 tasks");
      return;
    }

    const updatedTask = {
      ...formData,
      assignedTo: userId,
      priority: defaultPriority,
      status: defaultStatus,
      projectId: projectId,
      videoUrl: formData.videoUrl || "",
    };

    if (editIndex !== null) {
      const updatedTasks = tasks.map((task, index) =>
        index === editIndex ? updatedTask : task
      );
      setTasks(updatedTasks);
      setEditIndex(null);
    } else {
      setTasks([...tasks, updatedTask]);
    }

    setFormData({
      description: "",
      priority: "low",
      assignedTo: "",
      dueDate: today,
      status: "todo",
      projectId: "",
      videoUrl: "",
      isUserVideoReq: false,
    });
  };

  const editTask = (index: number) => {
    const taskToEdit = tasks[index];
    setFormData(taskToEdit);
    setDefaultPriority(taskToEdit.priority);
    setDefaultStatus(taskToEdit.status);
    setUserId(taskToEdit.assignedTo);
    setProjectId(taskToEdit.projectId);
    setEditIndex(index);
  };

  useEffect(() => {
    localStorage.setItem("tasks", JSON.stringify(tasks));
  }, [tasks]);

  const removeTask = (index: number) => {
    const updatedTasks = tasks.filter((_, i) => i !== index);
    setTasks(updatedTasks);
  };

  const handleSubmit: SubmitHandler<IAddTaskSheet> = async () => {
    if (tasks.length === 0) {
      toast.error("Please add at least one task.");
      return;
    }

    setLoading(true);

    try {
      for (const task of tasks) {
        await ApiService({
          method: "POST",
          endpoint: apiPaths.createTaskSheet,
          data: task,
        });
      }

      toast.success("All tasks added successfully", { duration: 5000 });
      localStorage.removeItem("tasks");
      setTimeout(() => {
        navigate("/taskSheet");
      }, 1500);
    } catch (error) {
      toast.error(`Error - ${error}`, { duration: 5000 });
      console.error("Error adding tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/taskSheet");
  };

  return {
    loading,
    formData,
    tasks,
    handleChange,
    handleSubmit,
    handleCancel,
    setFormData,
    setDefaultPriority,
    defaultPriority,
    defaultStatus,
    setDefaultStatus,
    user,
    userId,
    project,
    projectId,
    setProjectId,
    setUserId,
    addOrUpdateTask,
    removeTask,
    editTask,
    editIndex,
    isVideo,
    setIsVideo,
  };
};

export default useAddTaskSheet;
